import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import useAmplitude from "@/hooks/useAmplitude";
import { useMutation } from "@apollo/client";
import { useOutletContext, useParams } from "react-router-dom";
import type { EventOutletContext } from "../../_layout";
import { DELETE_EXPENSE_DOCUMENT_MUTATION } from "../queries";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface DeleteDocumentModalProps {
  onClose: () => void;
  documentId: string;
  documentName: string;
  refetch: () => void;
}

export default function DeleteDocumentModal({ onClose, documentId, refetch }: DeleteDocumentModalProps) {
  const { Modal, close } = useModal();
  const outletContext = useOutletContext<EventOutletContext | undefined>();
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const [deleteExpenseDocument, { loading }] = useMutation(DELETE_EXPENSE_DOCUMENT_MUTATION, {
    onCompleted: async (data) => {
      if (data.deleteExpenseDocument.success) {
        showSnackbar("Document deleted");
        trackEvent(
          EVENT_ANALYTICS_EVENTS.expenseDocumentDeleted,
          getEventAnalyticsData({
            source: "payouts",
            eventId: outletContext?.eventData?.id,
            eventSlug,
            documentId,
            action: "delete"
          })
        );
        refetch();
        await close();
        onClose();
      } else {
        showErrorSnackbar(data.deleteExpenseDocument.message || "Failed to delete document");
      }
    },
    onError: () => {
      showErrorSnackbar("Document not deleted", { subtitle: "Please try again later." });
    }
  });

  const handleDelete = () => {
    deleteExpenseDocument({ variables: { id: documentId } });
  };

  return (
    <Modal
      onClosed={onClose}
      headline="Delete Document"
      action={
        <Button
          variant="filled"
          theme="error"
          icon="delete"
          onClick={handleDelete}
          disabled={loading}
          testId="confirm-delete-document-button"
        >
          Delete
        </Button>
      }
      hasFormElements={false}
    >
      <p>Are you sure you want to delete this document? This cannot be undone.</p>
    </Modal>
  );
}
